import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  ExternalLink,
  HeartPulse,
  Layers,
  Mail,
  Phone,
  Plane,
  Scale,
} from "lucide-react";
const OFFICIAL_PGD_URL = "https://ahtraining.co.uk/product-category/pgds/";

interface AlliedHealthTrainingPgdAppDetailsProps {
  isDarkMode?: boolean;
  onBack: () => void;
}

const pgdCategories = [
  {
    title: "Travel health & vaccination PGDs",
    description: "Travel and non-travel vaccination options.",
    icon: Plane,
    iconClassName: "bg-emerald-100 text-emerald-600",
  },
  {
    title: "Weight Loss PGD",
    description:
      "Personal and premises options for weight management services.",
    icon: Scale,
    iconClassName: "bg-amber-100 text-amber-600",
  },
  {
    title: "PGD bundles",
    description: "All-inclusive, weight & travel bundles for pharmacy services.",
    icon: Layers,
    iconClassName: "bg-violet-100 text-violet-600",
  },
];

export function AlliedHealthTrainingPgdAppDetails({
  isDarkMode = false,
  onBack,
}: AlliedHealthTrainingPgdAppDetailsProps) {
  const headingClass = `transition-colors duration-300 ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const bodyClass = `transition-colors duration-300 ${
    isDarkMode ? "text-gray-300" : "text-gray-600"
  }`;
  const mutedClass = `transition-colors duration-300 ${
    isDarkMode ? "text-gray-400" : "text-gray-500"
  }`;
  const linkClass =
    "inline-flex items-center gap-1 font-medium text-purple-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 rounded-sm";
  const cardClass = `border rounded-lg p-5 flex flex-col gap-4 transition-colors duration-300 ${
    isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
  }`;

  return (
    <div className="p-6 space-y-6">
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={
            isDarkMode ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""
          }
        >
          <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
          Back
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div
            className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${
              isDarkMode ? "bg-gray-700 text-sky-300" : "bg-sky-50 text-sky-600"
            }`}
          >
            <HeartPulse className="w-7 h-7" aria-label="Clinical icon" role="img" />
          </div>
          <div className="space-y-1">
            <h2 className={`text-2xl font-bold ${headingClass}`}>
              Allied Health Training PGDs
            </h2>
            <p className={`text-sm ${mutedClass}`}>by Allied Health Training</p>
            <Badge variant="secondary" className="text-xs">
              Clinical resources
            </Badge>
          </div>
        </div>
        <a
          href="https://ahtraining.co.uk/product-category/pgds/"
          target="_blank"
          rel="noopener noreferrer"
          className={`${linkClass} sm:mt-1`}
        >
          <ExternalLink className="w-4 h-4" aria-hidden="true" />
          Visit Website
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </div>

      <p className={`text-base leading-relaxed ${bodyClass}`}>
        Browse Patient Group Directions (PGDs) from Allied Health Training.
        Explore individual PGDs and bundles for pharmacy services, including
        vaccination, travel health and weight management.
      </p>

      <section className="space-y-4" aria-labelledby="aht-available-pgds">
        <h3 id="aht-available-pgds" className={`text-lg font-semibold ${headingClass}`}>
          Available PGDs
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pgdCategories.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.title} className={cardClass}>
                <div className="flex items-start gap-3">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${category.iconClassName}`}
                  >
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div className="space-y-1">
                    <h4 className={`font-semibold ${headingClass}`}>
                      {category.title}
                    </h4>
                    <p className={`text-sm ${bodyClass}`}>{category.description}</p>
                  </div>
                </div>
                <div className="mt-auto flex justify-end">
                  <a
                    href={OFFICIAL_PGD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    View
                    <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                    <span className="sr-only">
                      {category.title} on the Allied Health Training website (opens in a new tab)
                    </span>
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section
        aria-labelledby="aht-purchase-heading"
        className={`rounded-lg border p-5 space-y-4 transition-colors duration-300 ${
          isDarkMode
            ? "border-purple-800 bg-purple-950/40"
            : "border-purple-200 bg-purple-50"
        }`}
      >
        <div className="space-y-2">
          <h3 id="aht-purchase-heading" className={`text-lg font-semibold ${headingClass}`}>
            Purchase directly from Allied Health Training
          </h3>
          <p className={`text-sm ${bodyClass}`}>
            PGDs are purchased directly from Allied Health Training. Contact
            their team to discuss your requirements and complete your purchase.
            TITANVERSE does not process PGD orders.
          </p>
        </div>
        <ul className={`space-y-2 text-sm ${bodyClass}`}>
          <li className="flex items-center gap-2">
            <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
            Phone: [Phone number to be added]
          </li>
          <li className="flex items-center gap-2">
            <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
            Email: [Email address to be added]
          </li>
        </ul>
        <Button asChild className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white">
          <a href={OFFICIAL_PGD_URL} target="_blank" rel="noopener noreferrer">
            Browse PGDs
            <ExternalLink className="w-4 h-4 ml-2" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        </Button>
      </section>

      <p className={`text-xs ${mutedClass}`}>
        Product range and availability are managed by Allied Health Training.
        Visit their website for current details.
      </p>
    </div>
  );
}
